import { useNodeId } from "@xyflow/react";
import { useCallback, Suspense, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Icon, Text } from "@zapier/design-system";
import { Typeahead, type Key } from "@zapier/design-system-beta";
import {
  interfaceProjectAsyncAtomFamily,
  interfacesAsyncAtom,
  takeSnapshotAtom,
} from "@zapier/canvas-state";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { ErrorBoundary } from "../../ErrorBoundary";
import { SelectedItem } from "../SelectedItem";
import styles from "./FloatingInterfacesTypeahead.module.css";

type Props = {
  interfaceProjectId: string | undefined;
};

type InnerProps = {
  interfaceProjectId: string | undefined;
};

function InterfacesTypeaheadLoader() {
  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search interfaces"
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <Typeahead.Item key="loading" id="loading">
          Loading interfaces...
        </Typeahead.Item>
      </Typeahead>
    </div>
  );
}

function SelectedInterfaceSuspended({ interfaceProjectId }: InnerProps) {
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const nodeId = useNodeId()!;
  const selectedInterface = useAtomValue(
    interfaceProjectAsyncAtomFamily({
      interfaceProjectId: interfaceProjectId,
      nodeId,
    }),
  );

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        projectId: undefined,
        pageId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={selectedInterface?.name || "Interface not found"}
      icon={<Icon name="navInterfaces" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function InterfacesTypeaheadSuspended() {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const interfaces = useAtomValue(interfacesAsyncAtom);

  useFocusInput(inputRef);

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          projectId: key,
          pageId: undefined, // need to reset the page if we change interface project
        },
      });

      if (key) {
        // TODO: emitEventOnInterfaceConnect
      }
    },
    [takeSnapshot, updateNodeAndSave],
  );

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search interfaces"
        placeholder="Search interfaces"
        ref={inputRef}
        onSelectionChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        size="medium"
      >
        {interfaces.map((interfaceProject) => {
          return (
            <Typeahead.Item
              key={interfaceProject.id}
              id={interfaceProject.id}
              textValue={interfaceProject.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navInterfaces" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{interfaceProject.name}</Text>
              </div>
            </Typeahead.Item>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function InterfacesTypeahead({ interfaceProjectId }: Props) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<InterfacesTypeaheadLoader />}>
        {interfaceProjectId ? (
          <SelectedInterfaceSuspended interfaceProjectId={interfaceProjectId} />
        ) : (
          <InterfacesTypeaheadSuspended />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

export function FloatingInterfacesTypeahead({ interfaceProjectId }: Props) {
  return <InterfacesTypeahead interfaceProjectId={interfaceProjectId} />;
}
